/**
 * Single source of truth for the studio's portfolio.
 *
 * Both the home page's "Selected work" section and the dedicated `/work`
 * page import from here. To add or reorder projects, edit this file.
 */

export type ProjectCategory =
  | 'Framer Development'
  | 'Custom Development'
  | 'AI Integration'
  | 'SEO Optimization'
  | 'Migration'

export interface Project {
  title: string
  description: string
  /** Display tags shown on the card (e.g. "Framer", "SaaS"). */
  tags: string[]
  /**
   * Top-level disciplines used by the /work page filter. A project can
   * belong to more than one — e.g. an AI-powered platform that was also
   * custom-built shows up under both "AI Integration" and "Custom
   * Development".
   */
  categories: ProjectCategory[]
  image: string
  hoverImage?: string
  url?: string
  /**
   * Internal route to the long-form case study page for this project,
   * e.g. `/work/magnifiko`. When present, the project card links here
   * instead of the external live `url`.
   */
  caseStudy?: string
}

export const projects: Project[] = [
  {
    title: 'Magnifiko',
    description:
      'A multilingual website and custom menu-management dashboard for a French fine-dine restaurant — letting the team update their menu effortlessly while serving guests in multiple languages.',
    tags: ['Custom Dev', 'Multilingual', 'Restaurant'],
    categories: ['Custom Development'],
    image: '/images/Projects/Magnifiko/magnifiko%201.webp',
    hoverImage: '/images/Projects/Magnifiko/Magnifiko%202.webp',
    url: 'https://www.magnifiko.fr/',
    caseStudy: '/work/magnifiko',
  },
  {
    title: 'Venmate',
    description:
      'A full WordPress-to-Framer migration and complete redesign for a modern fintech brand — faster load times, refined UI, and a fully editable Framer CMS the team can run themselves.',
    tags: ['Framer', 'Migration', 'Fintech'],
    categories: ['Framer Development', 'Migration'],
    image: '/images/Venmate%20Preview%201.webp',
    hoverImage: '/images/Venmate%202.webp',
    url: 'https://venmate.io/',
  },
  {
    title: 'Nexus',
    description:
      'A modern SaaS platform with a clean dashboard, seamless onboarding, and real-time data — built in Framer for speed and polish.',
    tags: ['SaaS', 'Framer'],
    categories: ['Framer Development'],
    image: '/images/nexus-preview.webp',
    hoverImage: '/images/nexus-preview-hover.webp',
    url: 'https://nexusapp.io/',
  },
  {
    title: 'VVC Media',
    description:
      'A conversion-focused marketing site for a UK media agency — designed and developed in Framer with bold visuals and smooth interactions.',
    tags: ['Marketing', 'Framer'],
    categories: ['Framer Development'],
    image: '/images/Projects/vvc%20media/vvcmedia-preview.webp',
    hoverImage: '/images/vcc-media-preview-hover.webp',
    url: 'https://vvcmedia.co.uk/',
  },
  {
    title: 'Menu Ochel',
    description:
      'A custom-built digital menu platform for a French restaurant — full-stack development with real-time updates and elegant UI.',
    tags: ['Custom Dev', 'Full-Stack'],
    categories: ['Custom Development'],
    image: '/images/menu-ochel-preview.webp',
    hoverImage: '/images/menu-ochel-preview-hover.webp',
    url: 'https://menu.ochel.fr/',
  },
  {
    title: 'Dr. May',
    description:
      'An AI-powered healthcare platform with intelligent patient interactions — combining modern design with advanced AI integration.',
    tags: ['AI', 'Healthcare', 'Custom Dev'],
    categories: ['Custom Development', 'AI Integration'],
    image: '/images/Projects/Dr May/drmay-preview.webp',
    hoverImage: '/images/Projects/Dr May/drmay-preview-hover.webp',
    url: 'https://www.drmay.fr/',
  },
]

/**
 * Ordered list of category filters for the /work page sidebar.
 * Order matters — controls left-to-right / top-to-bottom display order.
 */
export const PROJECT_CATEGORIES: ProjectCategory[] = [
  'Framer Development',
  'Custom Development',
  'AI Integration',
  'SEO Optimization',
  'Migration',
]

/**
 * Hand-curated picks shown in the home page "Selected work" grid, in the
 * exact order they should appear. The titles below must match the
 * `title` of an entry in `projects` above (case-sensitive). To change
 * what the home page features, just edit this array — `app/page.tsx`
 * automatically reads from it via `getHomepageProjects()`.
 */
export const HOMEPAGE_PROJECTS: readonly string[] = [
  'Magnifiko',
  'Dr. May',
  'Nexus',
  'VVC Media',
]

/**
 * Look the curated titles up against `projects` and return them in the
 * order declared by `HOMEPAGE_PROJECTS`. Any title that doesn't match a
 * real project is silently dropped, so a typo never crashes the build —
 * it just shortens the row. Falls back to the first N projects only if
 * the curated list is empty, never as a "fix-the-typo" cover-up.
 */
export function getHomepageProjects(): Project[] {
  if (HOMEPAGE_PROJECTS.length === 0) return projects.slice(0, 4)
  return HOMEPAGE_PROJECTS
    .map((title) => projects.find((p) => p.title === title))
    .filter((p): p is Project => Boolean(p))
}
